import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextInputSection from '../WidgetEditComponents/TextInputSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import TestimonialCardStyleSelector from '../WidgetEditComponents/TestimonialCardStyleSelector';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import '../WidgetEditComponents/TestimonialManager.css';
import SupremeTestimonialsManagement from '../WidgetEditComponents/SupremeTestimonialsManagement';
import ImageTooBigPopup from '../WidgetEditComponents/ImageTooBigPopup';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SupremeTestimonialEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Image Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [uploadingTestimonialIndex, setUploadingTestimonialIndex] = useState(null);
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        const fakeEvent = {
            target: {
                name: 'layoutType',
                value: layoutId,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Effect Change Handler für Card Style
    const handleEffectChange = (fieldName, value) => {
        const fakeEvent = {
            target: {
                name: fieldName,
                value: value,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Testimonial Management
    const addTestimonial = () => {
        const currentTestimonials = editData.testimonials || [];
        const newTestimonial = {
            name: 'Name',
            info: 'Position, Company',
            quote: 'Great testimonial quote here...',
            avatar: '',
            website: ''
        };
        const updatedTestimonials = [...currentTestimonials, newTestimonial];
        
        const fakeEvent = {
            target: {
                name: 'testimonials',
                value: updatedTestimonials,
                type: 'array'
            }
        };
        handleInputChange(fakeEvent);
    };

    const removeTestimonial = (index) => {
        const currentTestimonials = editData.testimonials || [];
        const updatedTestimonials = currentTestimonials.filter((_, i) => i !== index);
        
        const fakeEvent = {
            target: {
                name: 'testimonials',
                value: updatedTestimonials,
                type: 'array'
            }
        };
        handleInputChange(fakeEvent);
    };

    const updateTestimonial = (index, field, value) => {
        const currentTestimonials = editData.testimonials || [];
        const updatedTestimonials = [...currentTestimonials];
        updatedTestimonials[index] = { ...updatedTestimonials[index], [field]: value };
        
        const fakeEvent = {
            target: {
                name: 'testimonials',
                value: updatedTestimonials,
                type: 'array'
            }
        };
        handleInputChange(fakeEvent);
    };

    const uploadTestimonialAvatar = async (file, index) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }

        setUploadingTestimonialIndex(index);
        
        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `testimonial_${index}_avatar`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    setUploadingTestimonialIndex(null);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateTestimonial(index, 'avatar', downloadURL);
                        setUploadingTestimonialIndex(null);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
            setUploadingTestimonialIndex(null);
        }
    };

    const uploadTestimonialBackground = async (file, index) => {
        if (!file || file.size > 2 * 1024 * 1024) {
            alert('File is too big (max. 2MB).');
            return;
        }

        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `testimonial_${index}_background`;
            const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('Upload error:', error);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateTestimonial(index, 'backgroundImage', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
        }
    };


    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'quoteColor',
                label: 'Quote',
                defaultValue: '#ffffff',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'nameColor',
                label: 'Names',
                defaultValue: '#2c3e50',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#4A90E2',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'infoColor',
                label: 'Info Text',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'testimonialColor',
                label: 'Testimonials',
                defaultValue: '#555555',
                icon: '/img/text.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'Main Quote',
            sizeField: 'quoteSize',
            weightField: 'quoteWeight',
            defaultSize: '24',
            defaultWeight: '400'
        },
        {
            label: 'Names',
            sizeField: 'nameSize',
            weightField: 'nameWeight',
            defaultSize: '16',
            defaultWeight: '600'
        },
        {
            label: 'Info Text',
            sizeField: 'infoSize',
            weightField: 'infoWeight',
            defaultSize: '12',
            defaultWeight: '400'
        },
        {
            label: 'Testimonials',
            sizeField: 'testimonialSize',
            weightField: 'testimonialWeight',
            defaultSize: '14',
            defaultWeight: '400'
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Zusammengefasste "vereinzelte" Regler
    // 🚀 CUSTOM LAYOUT PROPERTIES - Keine widget-spezifischen Layout Properties
    const customLayoutProperties = [];

    const advancedConfig = [
        // Widget-spezifische Properties
        {
            fieldName: 'avatarSize',
            label: 'Avatar Size',
            type: 'slider',
            defaultValue: 80,
            min: 50,
            max: 120,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            color: '#FF5722',
            description: 'Size of testimonial avatars'
        },

        // Display Group
        {
            fieldName: 'borderRadius',
            label: 'Border Radius',
            type: 'number',
            defaultValue: 0,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'backgroundImageOpacity',
            label: 'Background Opacity',
            type: 'number',
            defaultValue: 80,
            min: 10,
            max: 100,
            step: 10,
            unit: '%',
            icon: '/img/background.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Layout Selector */}
                <LayoutSelector
                    widgetType="supremeTestimonial"
                    selectedLayout={editData.layoutType || 'testimonial-cards'}
                    onLayoutChange={handleLayoutChange}
                />

                {/* 🚀 BACKGROUND IMAGE CONTAINER - direkt nach Layouts */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 80,
                            min: 10,
                            max: 100,
                            step: 10,
                            unit: '%',
                            description: 'Opacity of the background image',
                            convertValue: true
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* REMOVED: Widget Height & Avatar Size moved to Advanced Settings */}

                {/* Card Style Selector - nur bei Cards Layout */}
                {editData.layoutType === 'testimonial-cards' && (
                    <TestimonialCardStyleSelector
                        selectedStyle={editData.cardStyle || 'classic'}
                        onStyleChange={(styleId) => handleEffectChange('cardStyle', styleId)}
                    />
                )}



                {/* Supreme Testimonials Management */}
                <SupremeTestimonialsManagement 
                    testimonials={editData.testimonials || []}
                    addTestimonial={addTestimonial}
                    removeTestimonial={removeTestimonial}
                    updateTestimonial={updateTestimonial}
                    uploadTestimonialAvatar={uploadTestimonialAvatar}
                    uploadingTestimonialIndex={uploadingTestimonialIndex}
                    editData={editData}
                />

                {/* OLD Testimonials Management */}
                <div className="testimonial-manager-container" style={{ display: 'none' }}>
                    <div className="testimonial-manager-header">
                        <div>
                            <h4 className="section-title" style={{ margin: 0 }}>Testimonials Management</h4>
                            <span className="testimonial-counter">{(editData.testimonials || []).length} Testimonials</span>
                        </div>
                        <button 
                            type="button"
                            className="add-testimonial-button"
                            onClick={addTestimonial}
                        >
                            Add Testimonial
                        </button>
                    </div>

                    {/* Testimonials Liste */}
                    {(editData.testimonials || []).map((testimonial, index) => (
                        <div key={index} className="testimonial-edit-item">
                            
                            <div className="testimonial-header">
                                <h5 className="testimonial-title">
                                    Testimonial {index + 1}
                                </h5>
                                <button 
                                    type="button"
                                    className="remove-testimonial-button"
                                    onClick={() => removeTestimonial(index)}
                                    title="Remove Testimonial"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Upload Section */}
                            <div className="testimonial-upload-section">
                                {/* Avatar Upload */}
                                <div className="testimonial-upload-box">
                                    {testimonial.avatar ? (
                                        <img src={testimonial.avatar} alt="Avatar" className="upload-preview-image" style={{ borderRadius: '50%' }} />
                                    ) : (
                                        <div className="upload-placeholder">👤</div>
                                    )}
                                    <p className="upload-label-text">Avatar</p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            if (e.target.files[0]) {
                                                uploadTestimonialAvatar(e.target.files[0], index);
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                        id={`avatar-upload-${index}`}
                                    />
                                    <label htmlFor={`avatar-upload-${index}`} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, cursor: 'pointer' }} />
                                    {uploadingTestimonialIndex === index && (
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                            <div className="spinner-container">
                                                <div className="spinner-circle"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Background Upload */}
                                <div className="testimonial-upload-box">
                                    {testimonial.backgroundImage ? (
                                        <img src={testimonial.backgroundImage} alt="Background" className="upload-preview-image" />
                                    ) : (
                                        <div className="upload-placeholder">🖼️</div>
                                    )}
                                    <p className="upload-label-text">Background</p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            if (e.target.files[0]) {
                                                uploadTestimonialBackground(e.target.files[0], index);
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                        id={`background-upload-${index}`}
                                    />
                                    <label htmlFor={`background-upload-${index}`} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, cursor: 'pointer' }} />
                                </div>
                            </div>

                            {/* Name */}
                            <div className="input-group">
                                <label>Name:</label>
                                <input 
                                    type="text" 
                                    value={testimonial.name || ''}
                                    onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                                    className="widgetInputField"
                                    placeholder="Serena Williams"
                                />
                            </div>

                            {/* Info */}
                            <div className="input-group">
                                <label>Info:</label>
                                <input 
                                    type="text" 
                                    value={testimonial.info || ''}
                                    onChange={(e) => updateTestimonial(index, 'info', e.target.value)}
                                    className="widgetInputField"
                                    placeholder="4-facher NBA-Champion, 5-facher All-Star"
                                />
                            </div>

                            {/* Main Quote */}
                            <div className="input-group">
                                <label>Main Quote:</label>
                                <textarea 
                                    value={testimonial.quote || ''}
                                    onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                                    className="widgetDescriptionField"
                                    rows="3"
                                    placeholder="Tony Robbins hat mir geholfen, herauszufinden..."
                                />
                            </div>

                            {/* Website */}
                            <div className="input-group">
                                <label>Website (optional):</label>
                                <input 
                                    type="url" 
                                    value={testimonial.website || ''}
                                    onChange={(e) => updateTestimonial(index, 'website', e.target.value)}
                                    className="widgetInputField"
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    ))}
                </div>
                </div>

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* More Settings (Colors & Fonts only) */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Advanced Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />
                
            </div>
    );
}

export default SupremeTestimonialEdit;
